/**
 * AdminReports — /admin/reports
 * Date picker → load daily report → metrics table → Download CSV.
 */
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminGetDailyReport, type DailyReport } from '../../services/api';
import { formatZAR } from '../../utils/currency';

interface AdminCtx {
  adminEmail: string | null;
}

function downloadCSV(report: DailyReport) {
  const rows = [
    ['Metric', 'Value'],
    ['Date', report.date],
    ['Total Bets', String(report.total_bets)],
    ['Total Stake (cents)', String(report.total_stake_cents)],
    ['Total Payout (cents)', String(report.total_payout_cents)],
    ['GGR (cents)', String(report.ggr_cents)],
    ['New Users', String(report.new_users)],
    ['Active Bettors', String(report.active_bettors)],
  ];
  const csv = rows.map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report-${report.date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReports() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = async () => {
    if (!adminEmail) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetDailyReport(adminEmail, date);
      setReport(res.data);
    } catch {
      setError('Failed to load report. Check the date.');
    } finally {
      setLoading(false);
    }
  };

  const metrics: Array<{ label: string; value: string }> = report
    ? [
        { label: 'Total Bets', value: report.total_bets.toLocaleString() },
        { label: 'Handle (Stake)', value: formatZAR(report.total_stake_cents) },
        { label: 'Total Payouts', value: formatZAR(report.total_payout_cents) },
        { label: 'GGR', value: formatZAR(report.ggr_cents) },
        { label: 'New Users', value: report.new_users.toLocaleString() },
        { label: 'Active Bettors', value: report.active_bettors.toLocaleString() },
      ]
    : [];

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Reports</h1>

      <div className="mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
          />
        </div>
        <button
          onClick={loadReport}
          disabled={loading}
          className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-60"
        >
          {loading ? 'Loading...' : 'Load report'}
        </button>
        {report && (
          <button
            onClick={() => downloadCSV(report)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Download CSV
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {report && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 px-6 py-4">
            <h2 className="font-semibold text-gray-800">
              Report for {report.date}
            </h2>
          </div>
          <dl className="divide-y divide-gray-100">
            {metrics.map((m) => (
              <div key={m.label} className="flex items-center justify-between px-6 py-4">
                <dt className="text-sm text-gray-500">{m.label}</dt>
                <dd className="font-medium text-gray-900 tabular-nums">{m.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {!report && !loading && !error && (
        <p className="text-sm text-gray-400">Select a date and click "Load report".</p>
      )}
    </div>
  );
}
